import React, { useState, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { Checkbox, SearchInput, INPUT_SIZES } from "cdk-radial";
import CircleXIcon from "../components/CircleXIcon";
import withNavigation from "../components/withNavigation";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import "./RoleDetail.css";

const RoleDetail = () => {
  const location = useLocation();
  const history = useHistory();

  const role = location.state?.role;
  const allUsers = location.state?.allUsers;
  const [searchTerm, setSearchTerm] = useState("");
  const [showEntitlements, setShowEntitlements] = useState(false);

  if (!role || !allUsers) {
    return (
      <div className="permission-detail-wrapper">
        <p>
          Role information not found. Please navigate from the User Role Management page.
        </p>
        <button onClick={() => history.push("/")}>Back to Home</button>
      </div>
    );
  }

  const lowerSearch = searchTerm.toLowerCase().trim();

  const filteredUsers = useMemo(() => {
    if (!lowerSearch) return allUsers;
    return allUsers.filter(
      (user) =>
        user.displayName.toLowerCase().includes(lowerSearch) ||
        user.email.toLowerCase().includes(lowerSearch) ||
        (user.org && user.org.toLowerCase().includes(lowerSearch))
    );
  }, [allUsers, lowerSearch]);

  const downloadCSV = () => {
    const headers = ["User", "Email", "Org", "Has Role"];
    const rows = filteredUsers.map((u) => [
      `"${u.displayName}"`,
      u.email,
      u.org || "N/A",
      u.roleIds?.includes(role.id) ? "Y" : "N",
    ]);
    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `role_users_${role.name.replace(/\s+/g, "_")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="permission-detail-wrapper expanded">
      <div className="user-perm-header">
        <h1 className="permission-title">{role.name}</h1>
      </div>

      <div className="entitlements-dropdown">
        <button
          className="entitlements-toggle"
          onClick={() => setShowEntitlements(!showEntitlements)}
        >
          Included Entitlements {showEntitlements ? "▲" : "▼"}
        </button>
        {showEntitlements && role.permissions?.length > 0 && (
          <div className="entitlements-content">
            {role.permissions.map((perm, idx) => (
              <div key={idx} className="entitlement-item">
                {perm.name}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="user-details-label">User Details</div>

      <div className="search-and-download">
        <SearchInput
          clearButtonLabel="Clear"
          id="search-user-role"
          name="search-user-role"
          isAlwaysExpanded
          placeholder="Search by user name or email..."
          size={INPUT_SIZES.STANDARD}
          value={searchTerm}
          onChange={(val) => setSearchTerm(val || "")}
          className="search-input"
        />
        <button className="download-csv-button" onClick={downloadCSV}>
          Download Report
        </button>
      </div>

      <div className="permission-detail-content">
        <div className="user-list-scroll expanded-scroll">
          <table className="user-list-table fixed-layout-table">
            <colgroup>
              <col style={{ width: "100px" }} />
              <col style={{ width: "240px" }} />
              <col style={{ width: "220px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "180px" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ textAlign: "center" }}>Has Role</th>
                <th>User Details</th>
                <th>Email Id</th>
                <th>Org</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const hasRole = user.roleIds?.includes(role.id) ?? false;
                const lastUpdatedDisplay = user.lastUpdated || "N/A";

                return (
                  <tr key={user.userId}>
                    <td style={{ textAlign: "center" }}>
                      {hasRole ? (
                        <Checkbox
                          name={`checkbox-${user.userId}`}
                          checked={true}
                          disabled={true}
                          onChange={() => {}}
                        />
                      ) : (
                        <CircleXIcon className="circle-x-icon" />
                      )}
                    </td>
                    <td>{user.displayName}</td>
                    <td>{user.email}</td>
                    <td>{user.org || "N/A"}</td>
                    <td>{lastUpdatedDisplay}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RoleDetailWithNav = withNavigation([
    {
    text: "Back to Home",
    goTo: "/"
  },
  {
    text: "Back to User Role Management",
    goTo: "/user-role-management"
  }
])(RoleDetail);

const RoleDetailContainer = (props) =>
  props.auth?.isAuthenticated ? (
    <RoleDetailWithNav {...props} />
  ) : (
    <div />
  );

export default withAuth(RoleDetailContainer);